import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Star, Check, Minus } from "lucide-react";
import { api, rupee } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { useCart } from "@/context/CartContext";

export default function Compare() {
  const { ids, toggle, clear } = useCompare();
  const { add } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (ids.length === 0) { setProducts([]); return; }
    api.get("/products/compare", { params: { ids: ids.join(",") } }).then(({ data }) => setProducts(data));
  }, [ids]);

  const rows = [
    { label: "Seller", get: (p) => p.seller_name },
    { label: "Price", get: (p) => rupee(p.discount ? Math.round(p.price * (1 - p.discount / 100)) : p.price) },
    { label: "Category", get: (p) => p.category },
    { label: "Delivery", get: (p) => `${p.delivery_days} days` },
    { label: "Refund policy", get: (p) => (p.refundable ? "Refundable" : "Non-refundable") },
    { label: "Rating", get: (p) => (p.review_count ? `${p.rating} (${p.review_count})` : "New") },
    { label: "Occasions", get: (p) => p.occasions.join(", ") || "—" },
  ];

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="overline text-clay mb-3">Side by side</div>
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">Compare gifts</h1>
          {products.length > 0 && <button onClick={clear} className="text-sm text-clay link-underline" data-testid="clear-compare-btn">Clear all</button>}
        </div>

        {products.length === 0 ? (
          <div className="py-24 text-center text-mute">
            No items to compare yet. <Link to="/shop" className="text-clay link-underline">Browse gifts</Link> and tap the compare icon (up to 4).
          </div>
        ) : (
          <div className="mt-10 overflow-x-auto">
            <div className="grid gap-4" style={{ gridTemplateColumns: `140px repeat(${products.length}, minmax(180px, 1fr))`, minWidth: 140 + products.length * 200 }}>
              <div />
              {products.map((p) => (
                <div key={p.id} className="relative" data-testid={`compare-col-${p.id}`}>
                  <button onClick={() => toggle(p.id)} className="absolute top-2 right-2 z-10 w-7 h-7 grid place-items-center rounded-full bg-sand/90 hover:bg-clay hover:text-sand" data-testid={`compare-remove-${p.id}`}><X className="w-4 h-4" /></button>
                  <Link to={`/product/${p.id}`} className="block aspect-[4/5] rounded-sm overflow-hidden bg-surface">
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                  </Link>
                  <Link to={`/product/${p.id}`}><h3 className="font-serif text-lg mt-3 leading-snug">{p.title}</h3></Link>
                </div>
              ))}

              {rows.map((row) => (
                <div key={row.label} className="contents">
                  <div className="overline text-mute py-4 border-t border-line flex items-center">{row.label}</div>
                  {products.map((p) => (
                    <div key={p.id + row.label} className="py-4 border-t border-line text-sm flex items-center gap-1">
                      {row.label === "Rating" && p.review_count > 0 && <Star className="w-4 h-4 fill-terra text-terra" />}
                      {row.label === "Refund policy" ? (p.refundable ? <span className="flex items-center gap-1 text-forest"><Check className="w-4 h-4" />Refundable</span> : <span className="flex items-center gap-1 text-clay"><Minus className="w-4 h-4" />Non-refundable</span>) : row.get(p)}
                    </div>
                  ))}
                </div>
              ))}

              <div className="border-t border-line py-4" />
              {products.map((p) => (
                <div key={p.id + "cta"} className="border-t border-line py-4">
                  <button onClick={() => add(p.id, 1)} className="w-full rounded-full bg-clay text-sand py-2.5 text-sm hover:bg-forest transition-colors" data-testid={`compare-add-${p.id}`}>Add to basket</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
